import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { Toast } from '@repo/videa-components';
import { toast } from 'sonner';
import { useLingui } from '@lingui/react/macro';

import type {
  ZuubManualCheckListPatient,
  ZuubManualCheckRequest,
} from '@/@types/zuub/contracts';
import { useZuubManualCheck } from '@/react-query/rcm/useZuubManualCheck';
import { useZuubManualCheckList } from '@/react-query/rcm/useZuubManualCheckList';
import { endpoints } from '@/react-query/endpoints';
import { parseDate } from '@/utils/parseDate';

// ── Types ──────────────────────────────────────────────────────────────

export interface FormFillValues {
  firstName: string;
  lastName: string;
  dob: Date | null;
  memberId: string;
  groupId: string;
  insuranceId: string;
  providerFirstName: string;
  providerLastName: string;
  dependent: {
    firstName: string;
    lastName: string;
    dob: Date | null;
    memberId: string;
    sameAsSubscriber: boolean;
  } | null;
}

export interface DuplicateCheckValues {
  firstName: string;
  lastName: string;
  dob: Date | null;
  memberId: string;
  payerId: string;
}

interface OptimisticEntry {
  patient: ZuubManualCheckListPatient;
  requestId: string | null;
  createdAt: number;
  tempId: string;
}

// ── Helpers ────────────────────────────────────────────────────────────

/** Returns true if a list entry is still loading (optimistic, processing, or sync-failed+async-pending). */
export const isEntryLoading = (patient: ZuubManualCheckListPatient): boolean => {
  const plan = patient.plans[0];
  if (!plan) return false;
  if (plan.syncStatus === null) return true;
  if (plan.syncStatus === 'failed' && plan.asyncStatus === 'pending') return true;
  return false;
};

const STALE_TIMEOUT_MS = 2 * 60 * 1000; // 2 minutes
const POLL_INTERVAL_MS = 5_000;
const PAGE_LIMIT = 10;

const parseDateOfBirth = (iso: string | null): Date | null => {
  if (!iso) return null;
  try {
    return parseDate(iso);
  } catch {
    return null;
  }
};

const deriveFormFillValues = (
  patient: ZuubManualCheckListPatient,
): FormFillValues => ({
  firstName: patient.subscriberFirstName ?? '',
  lastName: patient.subscriberLastName ?? '',
  dob: parseDateOfBirth(patient.dateOfBirth ?? null),
  memberId: patient.subscriberId ?? '',
  groupId: patient.groupId ?? '',
  insuranceId: patient.zuubPayerId != null ? String(patient.zuubPayerId) : '',
  providerFirstName: patient.providerFirstName ?? '',
  providerLastName: patient.providerLastName ?? '',
  dependent: patient.dependents?.[0]
    ? {
        firstName: patient.dependents[0].firstName,
        lastName: patient.dependents[0].lastName,
        dob: parseDateOfBirth(patient.dependents[0].dateOfBirth),
        memberId: patient.dependents[0].memberId,
        sameAsSubscriber: patient.dependents[0].sameAsSubscriber,
      }
    : null,
});

/** Compare dates by value. Uses toISOString for consistent comparison. */
const formatDateForCompare = (date: Date | null): string =>
  date ? date.toISOString().slice(0, 10) : '';

// ── Hook ───────────────────────────────────────────────────────────────

export const useRunVerification = () => {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const { mutateAsync: submitManualCheck } = useZuubManualCheck();

  // ── State ──
  const [selectedPatient, setSelectedPatient] =
    useState<ZuubManualCheckListPatient | null>(null);
  const [optimisticEntries, setOptimisticEntries] = useState<OptimisticEntry[]>(
    [],
  );
  const [currentPage, setCurrentPage] = useState(1);

  // ── Polling: use ref to carry forward previous render's decision ──
  const shouldPollRef = useRef(false);

  const {
    data: listData,
    isLoading: isListLoading,
    isError: isListError,
  } = useZuubManualCheckList({
    page: currentPage,
    limit: PAGE_LIMIT,
    refetchInterval:
      currentPage === 1 && (shouldPollRef.current || optimisticEntries.length > 0)
        ? POLL_INTERVAL_MS
        : false,
  });

  const realPatients = useMemo(
    () => listData?.patients ?? [],
    [listData],
  );
  const anyRealLoading = realPatients.some(isEntryLoading);

  // ── Reconcile optimistic entries with real data (pure computation) ──
  const reconciledOptimistic = useMemo(() => {
    if (optimisticEntries.length === 0) return [];

    return optimisticEntries.filter((opt) => {
      // Primary match: by requestId
      if (opt.requestId) {
        const match = realPatients.find((p) =>
          p.plans.some((plan) => plan.requestId === opt.requestId),
        );
        if (match) return false;
      }
      // Fallback: match by subscriber info
      const optP = opt.patient;
      const match = realPatients.find(
        (p) =>
          p.subscriberFirstName === optP.subscriberFirstName &&
          p.subscriberLastName === optP.subscriberLastName &&
          p.dateOfBirth === optP.dateOfBirth &&
          p.zuubPayerId === optP.zuubPayerId,
      );
      return !match;
    });
  }, [optimisticEntries, realPatients]);

  // ── Cleanup stale optimistic entries (side effect in useEffect, NOT useMemo) ──
  useEffect(() => {
    if (optimisticEntries.length === 0) return;
    const now = Date.now();
    const stale = optimisticEntries.filter(
      (e) => now - e.createdAt >= STALE_TIMEOUT_MS,
    );
    if (stale.length > 0) {
      setOptimisticEntries((prev) =>
        prev.filter((e) => now - e.createdAt < STALE_TIMEOUT_MS),
      );
      toast(
        <Toast
          variant="error"
          title={t`Verification timed out`}
          description={t`One or more verifications may have failed. Please try again.`}
        />,
        { closeButton: true, position: 'top-right', style: { width: '460px' } },
      );
    }
  }, [optimisticEntries, t]);

  // ── Merged + sorted entries ──
  const mergedEntries = useMemo(() => {
    const optimisticPatients =
      currentPage === 1 ? reconciledOptimistic.map((o) => o.patient) : [];

    const all = [...optimisticPatients, ...realPatients];
    return all.sort((a, b) => {
      const aLoading = isEntryLoading(a);
      const bLoading = isEntryLoading(b);
      if (aLoading && !bLoading) return -1;
      if (!aLoading && bLoading) return 1;
      const aTime = a.lastRun ? new Date(a.lastRun).getTime() : 0;
      const bTime = b.lastRun ? new Date(b.lastRun).getTime() : 0;
      return bTime - aTime;
    });
  }, [reconciledOptimistic, realPatients, currentPage]);

  const totalPages = Math.max(1, Math.ceil((listData?.totalCount ?? 0) / PAGE_LIMIT));

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  // ── Update polling ref for next render ──
  shouldPollRef.current = reconciledOptimistic.length > 0 || anyRealLoading;

  // ── Form fill ──
  const formFillValues = useMemo<FormFillValues | null>(
    () => (selectedPatient ? deriveFormFillValues(selectedPatient) : null),
    [selectedPatient],
  );

  // ── Duplicate check ──
  const checkDuplicate = useCallback(
    (values: DuplicateCheckValues): boolean => {
      if (!selectedPatient) return false;
      return (
        values.firstName.trim().toLowerCase() ===
          (selectedPatient.subscriberFirstName ?? '').toLowerCase() &&
        values.lastName.trim().toLowerCase() ===
          (selectedPatient.subscriberLastName ?? '').toLowerCase() &&
        formatDateForCompare(values.dob) ===
          formatDateForCompare(parseDateOfBirth(selectedPatient.dateOfBirth ?? null)) &&
        values.memberId.trim() === (selectedPatient.subscriberId ?? '') &&
        values.payerId === (selectedPatient.zuubPayerId != null
          ? String(selectedPatient.zuubPayerId)
          : '')
      );
    },
    [selectedPatient],
  );

  // ── Select patient ──
  const handleSelectPatient = useCallback(
    (patient: ZuubManualCheckListPatient) => {
      setSelectedPatient(patient);
    },
    [],
  );

  // ── Submit ──
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleVerifySubmit = useCallback(
    async (request: ZuubManualCheckRequest) => {
      const tempId = `optimistic-${Date.now()}`;
      const optimisticPatient: ZuubManualCheckListPatient = {
        unifiedAppointmentId: tempId,
        pmsPatientId: '',
        primaryProviderId: null,
        patientName: `${request.subscriberFirstName} ${request.subscriberLastName}`,
        appointmentDate: null,
        lastRun: new Date().toISOString(),
        hasMultiplePlans: false,
        totalPlans: 1,
        dateOfBirth: request.subscriberDateOfBirth,
        subscriberFirstName: request.subscriberFirstName,
        subscriberLastName: request.subscriberLastName,
        subscriberId: request.subscriberId,
        groupId: request.groupId ?? null,
        zuubPayerId: request.payerId,
        dependents: request.dependents,
        providerFirstName: request.providerFirstName,
        providerLastName: request.providerLastName,
        plans: [
          {
            unifiedAppointmentId: tempId,
            requestId: tempId,
            pmsPatientId: '',
            payerId: request.payerId,
            needsPayerPortalConnection: false,
            payerName: '',
            insuranceStatusV2: null,
            statusType: null,
            patientDisplayName: `${request.subscriberFirstName} ${request.subscriberLastName}`,
            remainingBenefits: null,
            remainingDeductible: null,
            totalDeductible: null,
            verificationStatus: null,
            resultSource: null,
            source: null,
            mismatches: null,
            order: 0,
            eligibility: [],
            syncStatus: null,
            asyncStatus: null,
          },
        ],
      };

      const entry: OptimisticEntry = {
        patient: optimisticPatient,
        requestId: null,
        createdAt: Date.now(),
        tempId,
      };

      setCurrentPage(1);
      setOptimisticEntries((prev) => [entry, ...prev]);
      setIsSubmitting(true);

      try {
        const response = await submitManualCheck(request);
        setOptimisticEntries((prev) =>
          prev.map((e) =>
            e.tempId === tempId
              ? { ...e, requestId: response.requestId }
              : e,
          ),
        );
        await queryClient.invalidateQueries([endpoints.zuubManualCheckList]);
      } catch {
        setOptimisticEntries((prev) => prev.filter((e) => e.tempId !== tempId));
        toast(
          <Toast
            variant="error"
            title={t`Verification failed`}
            description={t`Failed to submit verification. Please try again.`}
          />,
          { closeButton: true, position: 'top-right', style: { width: '460px' } },
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [submitManualCheck, queryClient, t],
  );

  return {
    selectedPatient,
    mergedEntries,
    selectedPatientId: selectedPatient?.unifiedAppointmentId ?? null,
    handleSelectPatient,
    isListLoading,
    isListError,
    formFillValues,
    checkDuplicate,
    handleVerifySubmit,
    isSubmitting,
    currentPage,
    totalPages,
    handlePageChange,
  };
};
